import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { HeatSystem } from "../components/HeatSystem";
import {
  ArrowLeft, ShieldCheck, Bot, Trash2, Pencil, UserX, Lock,
  Radar, RefreshCcw, Zap, AtSign,
} from "lucide-react";

const ICONS = {
  shield: ShieldCheck,
  bot: Bot,
  trash: Trash2,
  pencil: Pencil,
  "users-x": UserX,
  lock: Lock,
  zap: Zap,
  "at-sign": AtSign,
};

const SEV = {
  critical: { label: "Critical", bg: "bg-[#EF4444]/15", text: "text-[#EF4444]", ring: "border-[#EF4444]/40" },
  high:     { label: "High",     bg: "bg-[#F59E0B]/15", text: "text-[#F59E0B]", ring: "border-[#F59E0B]/40" },
  medium:   { label: "Medium",   bg: "bg-[#8B5CF6]/15", text: "text-[#8B5CF6]", ring: "border-[#8B5CF6]/40" },
};

export default function Protection() {
  const { guildId } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState([]);
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [guildName, setGuildName] = useState("");

  const load = async () => {
    try {
      const [prot, guild] = await Promise.all([
        api.get(`/guilds/${guildId}/protection`),
        api.get(`/guilds/${guildId}`).catch(() => ({ data: null })),
      ]);
      setMeta(prot.data.meta || []);
      setFeatures(prot.data.features || {});
      setGuildName(guild.data?.guild?.name || "");
    } catch (e) {
      toast.error("Failed to load protection settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [guildId]);

  const toggle = async (key, next) => {
    setSaving(s => ({ ...s, [key]: true }));
    // Optimistic update
    setFeatures(f => ({ ...f, [key]: next }));
    try {
      const r = await api.put(`/guilds/${guildId}/protection/${key}`, { enabled: next });
      setFeatures(r.data.features);
      const side = r.data.side_effect;
      if (side?.channels_affected != null) {
        toast.success(`${next ? "Locked" : "Unlocked"} ${side.channels_affected} channels`);
      } else {
        toast.success(`${next ? "Enabled" : "Disabled"} — ${key.replaceAll("_", " ")}`);
      }
    } catch (e) {
      setFeatures(f => ({ ...f, [key]: !next }));
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally {
      setSaving(s => ({ ...s, [key]: false }));
    }
  };

  const activeCount = Object.values(features).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-[#06070A] mm-grid-bg text-white" data-testid="protection-page">
      <div className="absolute inset-0 mm-noise pointer-events-none" />

      {/* Top bar */}
      <div className="relative z-10 border-b border-[#8B5CF6]/20 bg-gradient-to-r from-[#8B5CF6]/5 to-transparent flex items-center justify-between px-6 py-3">
        <div className="flex items-center gap-4">
          <button
            data-testid="protection-back-btn"
            onClick={() => navigate(`/dashboard/${guildId}`)}
            className="p-2 border border-white/10 hover:border-[#8B5CF6] hover:text-[#8B5CF6] transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <Radar className="w-4 h-4 text-[#8B5CF6]" />
              <span className="font-display font-black tracking-tight">PROTECTION SYSTEM</span>
            </div>
            <div className="font-mono text-[10px] text-[#64748B] tracking-widest">
              {guildName ? guildName.toUpperCase() : "SERVER"} · SWEEP EVERY 45S
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden md:flex items-center gap-2 px-3 py-1.5 border border-white/10">
            <span className={`w-1.5 h-1.5 rounded-full ${activeCount ? "bg-[#10B981] shadow-[0_0_6px_#10B981]" : "bg-[#64748B]"}`} />
            <span className="font-mono text-[10px] tracking-widest text-[#94A3B8]">
              {activeCount} / {meta.length} GUARDS ACTIVE
            </span>
          </div>
          <button
            data-testid="protection-refresh-btn"
            onClick={load}
            className="p-2 border border-white/10 hover:border-[#8B5CF6] hover:text-[#8B5CF6]"
          >
            <RefreshCcw className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Header info */}
        <div className="mb-6">
          <h1 className="font-display font-black text-3xl md:text-4xl">Server Guards</h1>
          <p className="text-sm text-[#94A3B8] mt-2 max-w-2xl">
            Automated protection layer. Every 45 seconds ModNode scans this server's audit log and enforces the guards you enable — reverting deletions, blocking bots, and reacting to raids.
          </p>
        </div>

        {loading && <div className="font-mono text-sm text-[#8B5CF6]">// loading guards...</div>}

        {!loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="guard-grid">
            {meta.map(f => (
              <GuardCard
                key={f.key}
                feature={f}
                enabled={!!features[f.key]}
                saving={!!saving[f.key]}
                onToggle={next => toggle(f.key, next)}
              />
            ))}
          </div>
        )}

        {/* Footnote */}
        <div className="mt-10">
          <HeatSystem guildId={guildId} />
        </div>

        <div className="mt-8 border border-white/10 bg-[#0D0E15] p-4 text-xs text-[#94A3B8] leading-relaxed">
          <span className="font-mono text-[10px] tracking-widest text-[#8B5CF6]">// HOW IT WORKS</span>
          <div className="mt-2 space-y-1">
            <div>· Guards fire on new audit-log entries — historical events are ignored.</div>
            <div>· The <span className="text-white">server owner</span> is never fought (their actions are trusted).</div>
            <div>· <span className="text-[#EF4444]">Server Lockdown</span> toggles denying SEND_MESSAGES for @everyone across text channels — takes effect immediately.</div>
            <div>· <span className="text-[#F59E0B]">Anti-Raid</span> triggers lockdown when ≥5 members join within 60 seconds.</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function GuardCard({ feature, enabled, saving, onToggle }) {
  const Icon = ICONS[feature.icon] || ShieldCheck;
  const sev = SEV[feature.severity] || SEV.medium;

  return (
    <div
      data-testid={`guard-card-${feature.key}`}
      className={`relative bg-[#0D0E15] border ${enabled ? "border-white/20" : "border-white/10"} p-5 transition-colors hover:border-white/30`}
    >
      {enabled && (
        <span className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#10B981] shadow-[0_0_10px_rgba(139,92,246,0.6)]" />
      )}
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div className={`shrink-0 w-11 h-11 border ${sev.ring} ${sev.bg} flex items-center justify-center`}>
            <Icon className={`w-5 h-5 ${sev.text}`} />
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold text-base leading-tight">{feature.label}</div>
            <span className={`inline-flex mt-1.5 px-2 py-0.5 text-[10px] font-mono tracking-widest uppercase ${sev.bg} ${sev.text} border ${sev.ring}`}>
              {sev.label}
            </span>
          </div>
        </div>
        <Toggle
          testId={`guard-toggle-${feature.key}`}
          enabled={enabled}
          disabled={saving}
          onChange={onToggle}
        />
      </div>
      <p className="text-xs text-[#94A3B8] leading-relaxed mb-3">{feature.desc}</p>
      <div className="flex items-center gap-2 pt-2 border-t border-white/5">
        <span className={`w-1.5 h-1.5 rounded-full ${enabled ? "bg-[#10B981] shadow-[0_0_6px_#10B981]" : "bg-[#64748B]"}`} />
        <span
          data-testid={`guard-status-${feature.key}`}
          className={`font-mono text-[10px] tracking-widest uppercase ${enabled ? "text-[#10B981]" : "text-[#64748B]"}`}
        >
          {enabled ? "Active" : "Disabled"}
        </span>
      </div>
    </div>
  );
}

function Toggle({ enabled, disabled, onChange, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      role="switch"
      aria-checked={enabled}
      disabled={disabled}
      onClick={() => !disabled && onChange(!enabled)}
      className={`relative shrink-0 w-11 h-6 border transition-all duration-200 ${
        enabled
          ? "bg-[#10B981] border-[#10B981] shadow-[0_0_10px_rgba(139,92,246,0.4)]"
          : "bg-[#141622] border-white/15"
      } ${disabled ? "opacity-50 cursor-wait" : "cursor-pointer"}`}
    >
      <span
        className={`absolute top-0.5 w-4 h-4 transition-all duration-200 ${
          enabled ? "left-6 bg-black" : "left-0.5 bg-white/80"
        }`}
      />
    </button>
  );
}
